import React, { useEffect } from "react";
import { StaticToolbar } from "./basic/Toolbar";
import { Toolbar } from "dhx-suite";
import { ShareWindow } from "./ShareWindow";
import { Mode } from "models";
import { parseQueryString, setSearchingParams } from "../utils/queryStringHelper";

const toolbarControls = [
  {
    id: "logo",
    css: "st-toolbar--logo",
    type: "customHTML",
    html: `<a href='https://dhtmlx.com/' title="" class="st-toolbar--logo-container">
                    <svg viewBox='0 0 40 18' xmlns='http://www.w3.org/2000/svg' class='logo'>
                        <path class='logo line' fill-rule='evenodd' d='M15.5 8.75C15.5 10.4 15.15 11.85 14.45 13.15C13.75 14.45 12.85 15.45 11.75 16.1C10.85 16.7 9.85 17.05 8.8 17.25C7.75 17.4 6.6 17.5 5.4 17.5H0V0H5.4C6.6 0 7.75 0.1 8.85 0.25C9.9 0.4 10.9 0.8 11.85 1.4C13 2.15 13.9 3.15 14.55 4.4C15.15 5.6 15.5 7.1 15.5 8.75ZM23 17.5L27 11.3V6L23 0V6.2H17.5V0H13V1.9C13.8 2.55 14.5 3.35 15 4.35C15.65 5.6 16 7.05 16 8.75C16 10.4 15.65 11.85 14.95 13.15C14.4 14.1 13.75 14.9 13 15.55V17.5H17.5V10.15H23V17.5ZM31.75 11.3L35.05 17.5H40L33.95 8.45L40 0H35.05L31.75 5.1L28.45 0H23.5L29 8.45L23.5 17.5H28.45L31.75 11.3ZM8.80005 3.89999C9.60005 4.34999 10.2 4.99999 10.6 5.79999C11 6.59999 11.2 7.59999 11.25 8.69999C11.25 9.89999 11.05 10.85 10.7 11.65C10.3 12.45 9.75005 13.1 9.05005 13.55C8.45005 13.95 7.80005 14.15 7.15005 14.2C6.45005 14.25 5.65005 14.25 4.65005 14.25H4.55005V3.29999H4.65005C5.55005 3.29999 6.30005 3.29999 6.95005 3.34999C7.60005 3.39999 8.20005 3.54999 8.80005 3.89999Z'/>
                    </svg>
                </a>
                <a href="https://snippet.dhtmlx.com/" class="st-toolbar--logo-label">Code Snippet</a>`,
  },
  {
    id: "add",
    type: "button",
    icon: "mdi mdi-plus",
    value: "New",
    css: "app-btn app-btn--add",
    circle: true,
    view: "link",
  },
  {
    id: "share",
    type: "button",
    icon: "mdi mdi-check",
    value: "Save",
    css: "app-btn app-btn--save",
    circle: true,
  },
  { type: "spacer" },
  {  type: "customHTML", id: "title", css: "st-toolbar--title" },
  { type: "spacer" },
  {
    id: "help",
    css: "st-toolbar--setting",
    tooltip: "Setting",
    type: "customButton",
    html: "<svg viewBox='0 0 16 15' xmlns='http://www.w3.org/2000/svg' class='setting-icon'><path class='setting-icon line' d='M0.5 11.6797V13.3203H5.5V11.6797H0.5ZM0.5 1.67969V3.32031H8.82031V1.67969H0.5ZM8.82031 15V13.3203H15.5V11.6797H8.82031V10H7.17969V15H8.82031ZM3.82031 5V6.67969H0.5V8.32031H3.82031V10H5.5V5H3.82031ZM15.5 8.32031V6.67969H7.17969V8.32031H15.5ZM10.5 5H12.1797V3.32031H15.5V1.67969H12.1797V0H10.5V5Z' /></svg>",
    circle: true,
    items: [
      {
        id: "light",
        value: "Light theme",
        icon: "mdi mdi-radiobox-blank",
        css: "st-menu-row st-radio-btn",
      },
      {
        id: "dark",
        value: "Dark theme",
        icon: "mdi mdi-radiobox-blank",
        css: "st-menu-row st-radio-btn",
      },
      { id: "separator", type: "separator", css: "st-menu-row" },
      { id: "mobile", value: "Mobile mode", css: "st-menu-row" },
      { id: "wide", value: "Wide mode", css: "st-menu-row" },
      { id: "basic", value: "Basic mode", css: "st-menu-row" },
      // { id: "separator", type: "separator", css: "st-menu-row" },
      // { id: "info", value: "Info", css: "st-menu-row st-toolbar--setting-info", icon: "mdi mdi-blank" }
    ],
  },
];

const TAG_ID_MARKER = "tbTag_";

function removeTags(toolbar: Toolbar) {
  const [logo, add, share, leftSpacer, title, ...tagsPlus] =
    toolbar.data.serialize();
  const [help, spacer, ...tags] = tagsPlus.reverse();

  const tagsIds = tags.map((t) => t.id!);
  tagsIds.forEach((id) => toolbar.data.remove(id));
}

interface TopToolbarProps {
  title?: string;
  tagsIds: number[];
  tags: Map<number, string>;
  isDarkTheme: boolean;
  onSaveClick: () => void;
  onTagClick: (tag: string) => void;
  onNewClick: () => void;
  onThemeSelect: (dark: boolean) => void;
  url: string;
  sharePopupVisible: boolean;
  onCopyClick: () => void;
  onSharePopupClose: () => void;
  mode: Mode;
}

export const TopToolbar = ({
  title,
  tagsIds,
  tags,
  isDarkTheme,
  onSaveClick,
  onTagClick,
  onNewClick,
  onThemeSelect,
  url,
  sharePopupVisible,
  onCopyClick,
  onSharePopupClose,
  mode,
}: TopToolbarProps) => {
  const [toolbar, setToolbar] = React.useState<Toolbar>();

  useEffect(() => {
    if (toolbar) {
      toolbar.data.update("light", {
        icon: `mdi mdi-radiobox-${isDarkTheme ? "blank" : "marked"}`,
        css: `st-menu-row st-radio-btn ${isDarkTheme ? "dark-row" : ""}`,
      });
      toolbar.data.update("dark", {
        icon: `mdi mdi-radiobox-${isDarkTheme ? "marked" : "blank"}`,
        css: `st-menu-row st-radio-btn ${isDarkTheme ? "dark-row" : ""}`,
      });
      toolbar.data.update("separator", {
        css: `st-menu-row ${isDarkTheme ? "dark-row" : ""}`,
      });
      toolbar.data.update("mobile", {
        css: `st-menu-row ${isDarkTheme ? "dark-row" : ""}`,
      });
      toolbar.data.update("wide", {
        css: `st-menu-row ${isDarkTheme ? "dark-row" : ""}`,
      });
      toolbar.data.update("basic", {
        css: `st-menu-row ${isDarkTheme ? "dark-row" : ""}`,
      });
      if (title) {
        toolbar.data.update("title", { html: `<h1 class="dhx_navbar-title dhx_navbar-title--toolbar">${title}</h1>` });
        toolbar.show("title");
      } else {
        toolbar.hide("title");
      }
    }
  }, [toolbar, title, isDarkTheme]);

  
  useEffect(() => {
    if (toolbar) {
      removeTags(toolbar);
      toolbar.data.add({
        type: "customButton",
        css: "st-toolbar--button__tags",
        id: "tags",
        html: tagsIds.map(tagId => {
          return `<span class="st-toolbar--button__tag" id="${TAG_ID_MARKER}${tags.get(tagId)}">${tags.get(tagId)}</span>`
        }).join("")
      }, 5);
    }
  }, [toolbar, tagsIds.join(","), tags]);

  useEffect(() => {
    if (!toolbar) return;
    switch (mode) {
      case "wide":
        toolbar.hide("wide");
        toolbar.show("basic");
        break;
      case "":
        toolbar.show("wide");
        toolbar.hide("basic");
        break;
    }
  }, [toolbar, mode]);

  return (
    <>
      <StaticToolbar
        controls={toolbarControls}
        className="st-toolbar"
        onToolbarMounted={setToolbar}
        onClick={(id, e) => {
          switch (id) {
            case "share":
              onSaveClick();
              break;
            case "add":
              onNewClick();
              break;
            case "dark":
              onThemeSelect(true);
              break;
            case "light":
              onThemeSelect(false);
              break;
            case "mobile":
              setSearchingParams(undefined, undefined, id)
              break;
            case "basic":
              const qs = parseQueryString(
                document.location.href.split("?")[1] || ""
              );
              setSearchingParams(qs.tag, qs.text, "");
              break;
            case "wide":
              const qstr = parseQueryString(
                document.location.href.split("?")[1] || ""
              );
              setSearchingParams(qstr.tag, qstr.text, id);
              break;

            case "tags":
              const target = e.target as HTMLElement;
              const tag = target.id
              if (tag.indexOf(TAG_ID_MARKER) === 0) {
                onTagClick(tag.replace(TAG_ID_MARKER, ""));
              }
              break;
          }
        }}
      />
      <ShareWindow
        url={url}
        visible={sharePopupVisible}
        onClose={onSharePopupClose}
        onCopyClick={onCopyClick}
        darkMode={isDarkTheme}
      />
    </>
  );
};
